import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { catchError, expand, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FetchPolicy } from '@apollo/client';
import { get } from 'lodash';
import * as forge from 'node-forge';

import { CertificateStorage, readFileAsBinaryString, StorageService, throwOnGraphqlError } from '@app/core';
import { SignatureTypesEnum, User, CloudSignaturesEnum, Document, Signer } from '@app/models';
import { ErrorHandlerService } from './error-handler.service';
import {
  AuthorizeCloudCertApplicationGQL,
  AuthorizeCloudCertApplicationMutationVariables,
  AuthorizeCloudCertSignatureGQL,
  AuthorizeCloudCertSignatureMutationVariables,
  CheckAuthorizationGQL,
  CheckAuthorizationMutationVariables,
  CurrentUserHasCloudCertGQL,
  GenerateSignatureSessionGQL,
  StartPadesSignatureGQL,
  StartPadesSignatureMutationVariables
} from 'src/generated/graphql.default';
import { SignatureStatusGQL } from 'src/generated/graphql.public';

export interface PadesSignatureData {
  type: SignatureTypesEnum;
  signer: Signer;
  document: Document;
  padesSignature?: string;
  isA3DesktopSuccess?: boolean;
}

export interface DesktopSignatureStatus {
  signed: boolean;
  timedOut: boolean;
}

@Injectable({ providedIn: 'root' })
export class PadesService {
  constructor(
    private storageService: StorageService,
    private errorHandlerService: ErrorHandlerService,
    private startPadesSignatureGQL: StartPadesSignatureGQL,
    private currentUserHasCloudCertGQL: CurrentUserHasCloudCertGQL,
    private authorizeCloudCertApplicationGQL: AuthorizeCloudCertApplicationGQL,
    private authorizeCloudCertSignatureGQL: AuthorizeCloudCertSignatureGQL,
    private checkAuthorizationGQL: CheckAuthorizationGQL,
    private generateSignatureSessionGQL: GenerateSignatureSessionGQL,
    private signatureStatusGQL: SignatureStatusGQL
  ) {}

  startPadesSignature(variables: StartPadesSignatureMutationVariables) {
    return this.startPadesSignatureGQL.mutate({ ...variables }).pipe(
      throwOnGraphqlError(),
      map(response => response.data.acceptSignature)
    );
  }

  authorizeCloudCertApp(variables: AuthorizeCloudCertApplicationMutationVariables) {
    return this.authorizeCloudCertApplicationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.authorizeApp)
    );
  }

  authorizeSafeIdSignature(variables: AuthorizeCloudCertSignatureMutationVariables) {
    return this.authorizeCloudCertSignatureGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.authorizeSign)
    );
  }

  checkCloudCert(options: { fetchPolicy: FetchPolicy } = { fetchPolicy: 'network-only' }) {
    return this.currentUserHasCloudCertGQL.fetch(null, options).pipe(
      throwOnGraphqlError(),
      map(response => response.data.hasCloudCert)
    );
  }

  checkAuthorization(variables: CheckAuthorizationMutationVariables): Observable<boolean> {
    return this.checkAuthorizationGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.checkAuthorization)
    );
  }

  generateSignatureSession(signatureId: string): Observable<string> {
    return this.generateSignatureSessionGQL.mutate({ signature_id: signatureId }).pipe(
      throwOnGraphqlError(),
      map(response => response.data.generateSignatureSession),
      switchMap(sessionId => (sessionId ? of(sessionId) : throwError(() => new Error('No session ID returned'))))
    );
  }

  checkDesktopSignatureStatus(signatureId: string): Observable<boolean> {
    return this.signatureStatusGQL.fetch({ documentId: signatureId }, { fetchPolicy: 'network-only' }).pipe(
      throwOnGraphqlError(),
      map(response => !!response.data.signature?.signed)
    );
  }

  startPollingDesktopSignatureStatus(
    signatureId: string,
    cancel$: Observable<void>,
    options: { initialDelayMs?: number; delayIncrementMs?: number; maxDelayMs?: number } = {}
  ): Observable<DesktopSignatureStatus> {
    const initialDelayMs = options.initialDelayMs ?? 5000;
    const delayIncrementMs = options.delayIncrementMs ?? 1000;
    const maxDelayMs = options.maxDelayMs ?? 34000;
    const fetchStatus = () =>
      this.checkDesktopSignatureStatus(signatureId).pipe(
        catchError(error => {
          this.errorHandlerService.handle(error, { ignoreNotificationOn: ['signature_not_found'] });
          return of(false);
        })
      );

    return timer(initialDelayMs).pipe(
      switchMap(() => fetchStatus()),
      map(signed => ({ signed, timedOut: false }) as DesktopSignatureStatus),
      expand((status, attemptIndex) => {
        if (status.signed || status.timedOut) {
          return EMPTY;
        }

        const nextDelayMs = initialDelayMs + (attemptIndex + 1) * delayIncrementMs;
        if (nextDelayMs > maxDelayMs) {
          return of({ signed: false, timedOut: true } as DesktopSignatureStatus);
        }

        return timer(nextDelayMs).pipe(
          switchMap(() => fetchStatus()),
          map(signed => ({ signed, timedOut: false }) as DesktopSignatureStatus)
        );
      }),
      takeUntil(cancel$)
    );
  }

  prepareToSignPades(options: {
    type: SignatureTypesEnum;
    user: User;
    signatureId: string;
    signer: Signer;
    document: Document;
    password?: string;
    file?: File | string;
    cpfCnpj?: string;
  }): Observable<PadesSignatureData> {
    if (options.type === SignatureTypesEnum.Safeid) {
      return (options.cpfCnpj && options.password ? this.authorizeSafeIdSignature({ cpfCnpj: options.cpfCnpj, password: options.password }) : of(true)).pipe(
        switchMap(isAuthorized => (isAuthorized ? of(null) : throwError(null))),
        tap(() => this.removeA1CertificateData(options.user)),
        map(() => ({ type: SignatureTypesEnum.Safeid, signer: options.signer, document: options.document }) as PadesSignatureData)
      );
    } else if (options.type === SignatureTypesEnum.A3) {
      return of(null).pipe(
        map(() => {
          electronAPI.openPkcs11Session(options.user, options.password);
          return electronAPI.getPkcs11Certificate();
        }),
        switchMap(certPem => this.startPadesSignature({ id: options.signatureId, type: SignatureTypesEnum.A3, payload: certPem })),
        map(data => data.hash),
        map(messageEnc => electronAPI.signPkcs11(messageEnc)),
        tap(() => this.removeA1CertificateData(options.user)),
        map(padesSignature => ({ type: SignatureTypesEnum.A3, padesSignature, signer: options.signer, document: options.document })),
        finalize(() => electronAPI.closePkcs11Session())
      );
    } else if ([SignatureTypesEnum.Vidaas, SignatureTypesEnum.Birdid, SignatureTypesEnum.Dscloud].includes(options.type)) {
      return of(null).pipe(
        tap(() => this.removeA1CertificateData(options.user)),
        map(() => ({ type: options.type, signer: options.signer, document: options.document }) as PadesSignatureData)
      );
    } else {
      let fileAsBinaryString: string;
      let certificate: forge.pki.Certificate;
      let privateKey: forge.pki.rsa.PrivateKey;
      return (typeof options.file === 'string' ? of(options.file) : readFileAsBinaryString(options.file)).pipe(
        tap(data => {
          fileAsBinaryString = data;
          certificate = this.parseA1Certificate(fileAsBinaryString, options.password);
          if (this.isCertificateExpired(certificate)) {
            throw new Error('certificate expired');
          } else {
            privateKey = this.parseA1PrivateKey(fileAsBinaryString, options.password);
          }
        }),
        map(() => forge.pki.certificateToPem(certificate)),
        switchMap(certPem => this.startPadesSignature({ id: options.signatureId, type: SignatureTypesEnum.A1, payload: certPem })),
        map(data => data.hash),
        map(messageEnc => {
          const md = forge.md.sha256.create();
          md.update(forge.util.hexToBytes(messageEnc));
          return forge.util.bytesToHex(privateKey.sign(md));
        }),
        tap(() => this.storeA1CertificateData(options.user, { file: fileAsBinaryString, password: options.password })),
        map(padesSignature => ({ type: SignatureTypesEnum.A1, padesSignature, signer: options.signer, document: options.document }))
      );
    }
  }

  selectPkcs11LibDialog() {
    return new Observable<string>(subscriber => {
      if (electronAPI) {
        electronAPI
          .selectPkcs11LibDialog()
          .then((filePath: string) => {
            subscriber.next(filePath);
            subscriber.complete();
          })
          .catch(subscriber.error);
      } else {
        subscriber.error();
      }
    });
  }

  isCertificateExpired(certificate: forge.pki.Certificate) {
    return new Date() < certificate.validity.notBefore || new Date() > certificate.validity.notAfter;
  }

  getStoredA1CertificateData(user: User) {
    const certData = this.storageService.getA1CertificateData(user);
    if (!certData?.file || !certData?.password) {
      return null;
    }

    try {
      const certificate = this.parseA1Certificate(certData.file, certData.password);

      if (!certificate || this.isCertificateExpired(certificate) || !this.parseA1PrivateKey(certData.file, certData.password)) {
        this.removeA1CertificateData(user);
        return null;
      }

      return certData;
    } catch {
      this.removeA1CertificateData(user);
      return null;
    }
  }

  storeA1CertificateData(user: User, value: CertificateStorage) {
    this.storageService.setA1CertificateData(user, value);
  }

  removeA1CertificateData(user: User) {
    this.storageService.removeA1CertificateData(user);
  }

  parseA1Certificate(fileAsBinaryString: string, password: string) {
    const certificates = this.getPkcs12(fileAsBinaryString, password).getBags({ bagType: forge.pki.oids.certBag })[forge.pki.oids.certBag];
    const issuers = certificates.map(crt => crt.cert.issuer.hash);
    return get(
      certificates.find(crt => !issuers.includes(crt.cert.subject.hash)),
      'cert'
    );
  }

  parseA1PrivateKey(fileAsBinaryString: string, password: string) {
    const pkcs12 = this.getPkcs12(fileAsBinaryString, password);
    const privateKey = get(pkcs12.getBags({ bagType: forge.pki.oids.keyBag })[forge.pki.oids.keyBag][0], 'key') as forge.pki.rsa.PrivateKey;
    return privateKey || (get(pkcs12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag })[forge.pki.oids.pkcs8ShroudedKeyBag][0], 'key') as forge.pki.rsa.PrivateKey);
  }

  private getPkcs12(fileAsBinaryString: string, password: string) {
    return forge.pkcs12.pkcs12FromAsn1(forge.asn1.fromDer(fileAsBinaryString), password);
  }
}
